import type {
  DraftDisplayProjection,
  ReportingMessage,
  ReportingRunEvent,
  ReportingRunProjection,
} from 'src/queries/reportingAgent/reportingAgent.models'

export type ReportingDraftStatus =
  | 'empty'
  | 'submitting'
  | 'running'
  | 'ready'
  | 'refining'
  | 'error'

export interface ReportingDraftError {
  code: string
  message: string
  retryable: boolean
}

export interface ReportingDraftState {
  status: ReportingDraftStatus
  draftRequestId: string | null
  conversationId: string | null
  messageId: string | null
  runId: string | null
  prompt: string
  messages: readonly ReportingMessage[]
  run: ReportingRunProjection | null
  artifact: DraftDisplayProjection | null
  messageIdempotencyKey: string | null
  error: ReportingDraftError | null
  stream: {
    status: 'idle' | 'connected' | 'reconnect-required' | 'stopped'
    lastSequence: number
    reconnectAfterSequence: number | null
  }
}

export const reportingDraftInitialState: ReportingDraftState = {
  status: 'empty',
  draftRequestId: null,
  conversationId: null,
  messageId: null,
  runId: null,
  prompt: '',
  messages: [],
  run: null,
  artifact: null,
  messageIdempotencyKey: null,
  error: null,
  stream: { status: 'idle', lastSequence: 0, reconnectAfterSequence: null },
}

export type ReportingDraftAction =
  | { type: 'entryInitialized'; draftRequestId: string }
  | { type: 'promptChanged'; prompt: string }
  | { type: 'submitStarted'; prompt: string; idempotencyKey: string }
  | {
      type: 'submissionAccepted'
      conversationId: string
      messageId: string
      runId: string
      status: 'queued' | 'running'
    }
  | {
      type: 'canonicalReconciled'
      conversationId: string
      messages: readonly ReportingMessage[]
      run: ReportingRunProjection | null
      artifact: DraftDisplayProjection | null
    }
  | { type: 'eventReceived'; event: ReportingRunEvent }
  | { type: 'streamReconnected'; afterSequence: number }
  | { type: 'activeRunConflict'; runId: string }
  | {
      type: 'refinementStarted'
      prompt: string
      idempotencyKey: string
      runId: string
    }
  | { type: 'artifactReady'; artifact: DraftDisplayProjection }
  | { type: 'runFailed'; code: string; message: string; retryable: boolean }
  | { type: 'authorizationLost' }

const errorFromEvent = (event: ReportingRunEvent): ReportingDraftError => {
  if (
    event.type === 'run.failed' &&
    'code' in event.payload &&
    typeof event.payload.code === 'string'
  ) {
    return {
      code: event.payload.code,
      message:
        typeof event.payload.message === 'string'
          ? event.payload.message
          : 'The report could not be completed.',
      retryable: event.payload.retryable === true,
    }
  }
  return {
    code: event.type,
    message: 'The report could not be completed.',
    retryable: false,
  }
}

export const reportingDraftReducer = (
  state: ReportingDraftState,
  action: ReportingDraftAction,
): ReportingDraftState => {
  switch (action.type) {
    case 'entryInitialized':
      return state.draftRequestId === action.draftRequestId
        ? state
        : { ...state, draftRequestId: action.draftRequestId }
    case 'submitStarted':
      return {
        ...state,
        status: 'submitting',
        prompt: action.prompt,
        error: null,
      }
    case 'promptChanged':
      return { ...state, prompt: action.prompt }
    case 'submissionAccepted':
      return {
        ...state,
        status: 'running',
        conversationId: action.conversationId,
        messageId: action.messageId,
        runId: action.runId,
        messageIdempotencyKey: null,
        error: null,
        stream:
          state.runId === action.runId
            ? { ...state.stream, status: 'connected' }
            : {
                status: 'connected',
                lastSequence: 0,
                reconnectAfterSequence: null,
              },
      }
    case 'canonicalReconciled': {
      const terminalRun =
        action.run?.status === 'completed' ||
        action.run?.status === 'failed' ||
        action.run?.status === 'cancelled'
      const terminalError =
        action.run?.status === 'failed' || action.run?.status === 'cancelled'
          ? (state.error ?? {
              code: action.run.status,
              message: 'The report could not be completed.',
              retryable: false,
            })
          : null
      const nextStatus = action.artifact
        ? terminalRun && action.run?.status !== 'completed'
          ? 'error'
          : 'ready'
        : terminalRun
          ? action.run?.status === 'completed'
            ? 'ready'
            : 'error'
          : action.run
            ? 'running'
            : 'empty'
      return {
        ...state,
        conversationId: action.conversationId,
        messages: action.messages,
        run: action.run,
        runId: action.run?.id ?? null,
        artifact: action.artifact,
        messageIdempotencyKey: null,
        status: nextStatus,
        error:
          terminalRun && action.run?.status !== 'completed'
            ? terminalError
            : action.artifact
              ? null
              : terminalError,
        stream: {
          ...state.stream,
          status: action.run && !terminalRun ? state.stream.status : 'idle',
        },
      }
    }
    case 'eventReceived': {
      const sequence = action.event.sequence
      if (sequence <= state.stream.lastSequence) return state
      const failed =
        action.event.type === 'run.failed' ||
        action.event.type === 'run.cancelled'
      return {
        ...state,
        status: failed
          ? 'error'
          : state.status === 'submitting'
            ? 'running'
            : state.status,
        error: failed ? errorFromEvent(action.event) : state.error,
        stream: {
          status: 'connected',
          lastSequence: sequence,
          reconnectAfterSequence: null,
        },
      }
    }
    case 'streamReconnected':
      return {
        ...state,
        stream: {
          status: 'connected',
          lastSequence: action.afterSequence,
          reconnectAfterSequence: null,
        },
      }
    case 'activeRunConflict':
      return {
        ...state,
        status: 'running',
        runId: action.runId,
        error: {
          code: 'conversation_run_active',
          message: 'This request is still running.',
          retryable: true,
        },
      }
    case 'refinementStarted':
      return {
        ...state,
        status: 'refining',
        prompt: action.prompt,
        runId: action.runId,
        messageIdempotencyKey:
          state.messageIdempotencyKey ?? action.idempotencyKey,
        error: null,
      }
    case 'artifactReady':
      return {
        ...state,
        status: 'ready',
        artifact: action.artifact,
        error: null,
      }
    case 'runFailed':
      return {
        ...state,
        status: 'error',
        error: {
          code: action.code,
          message: action.message,
          retryable: action.retryable,
        },
      }
    case 'authorizationLost':
      return {
        ...reportingDraftInitialState,
        stream: {
          status: 'stopped',
          lastSequence: 0,
          reconnectAfterSequence: null,
        },
      }
  }
}

export const requiresReportingDraftDiscardConfirmation = (
  state: ReportingDraftState,
): boolean => state.artifact !== null
